/**
 * HelpWidget — balão de atendimento PREMIUM (estilo Cakto, cores DarkPlanner).
 *
 * Visual redesenhado (header rico com gradiente da marca + logo + AVATAR do
 * atendente + saudação com efeito digitando, input central, última conversa +
 * "Central de Ajuda" na tela inicial, nav em pílula estreita) e chat com
 * AVATARES (foto do usuário / avatar do Planner). TODA a
 * lógica é a mesma da versão anterior — chat, cota, histórico, ArticleView e
 * endpoints intactos. Estado 100% em memória; axios global (token via interceptor).
 *
 * Tokens reais do app (DESIGN_SYSTEM.md / index.css):
 *   gradiente  linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)
 *   page #0A0A0A · card #141414 · elevado #1A1A1A · borda rgba(255,255,255,.06)
 *   texto #F5F5F5/#A0A0A0/#8A8A8A · logo /logo-darkplanner.png
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquareMore, X, Home, MessagesSquare, HelpCircle, Send, ArrowLeft, ArrowUp,
  Search, Loader2, ChevronRight, ChevronDown,
} from 'lucide-react';
import ArticleView from './ArticleView';
import CodeBlock from './CodeBlock';
import { categoryIcon } from './helpCategoryIcons';

// Markdown LEVE nas respostas do Planner (negrito, listas, [clique aqui](link)).
// skipHtml -> sem HTML cru (XSS-safe); links abrem em nova aba.
const CHAT_MD_COMPONENTS = {
  p: ({ children }) => <p style={{ margin: '0 0 8px' }}>{children}</p>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#0DAEFF', textDecoration: 'underline', fontWeight: 600 }}>{children}</a>
  ),
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  ul: ({ children }) => <ul style={{ margin: '0 0 8px', paddingLeft: '18px' }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 8px', paddingLeft: '18px' }}>{children}</ol>,
  li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
  code: ({ children }) => <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: '5px', fontSize: '12.5px' }}>{children}</code>,
  // Bloco de código (```) com botão COPIAR — comando do CMD colado sem erro.
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  // Imagem no chat (passo a passo ilustrado): imagem + LEGENDA (alt) embaixo.
  // span display:block (não div) porque o markdown coloca img dentro de <p>.
  img: ({ src, alt }) => (
    <span style={{ display: 'block', margin: '8px 0' }}>
      <img
        src={src} alt={alt || ''} loading="lazy" referrerPolicy="no-referrer"
        style={{ maxWidth: '100%', height: 'auto', borderRadius: '10px', display: 'block', border: '1px solid rgba(255,255,255,0.08)' }}
      />
      {alt && (
        <span style={{ display: 'block', fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: '4px', lineHeight: 1.4 }}>
          {alt}
        </span>
      )}
    </span>
  ),
  // Sem títulos/tabelas no chat: degrada pra texto normal.
  h1: ({ children }) => <p style={{ margin: '0 0 8px', fontWeight: 700 }}>{children}</p>,
  h2: ({ children }) => <p style={{ margin: '0 0 8px', fontWeight: 700 }}>{children}</p>,
  h3: ({ children }) => <p style={{ margin: '0 0 8px', fontWeight: 700 }}>{children}</p>,
};

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_CHARS = 1000;

// Tokens da marca (valores reais do DESIGN_SYSTEM.md).
const GRAD = 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)';
const GRAD_HOVER = 'linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%)';
// Gradiente do HEADER do widget — variante suave/vertical que melta no fundo embaixo.
const HEADER_GRAD = 'linear-gradient(168deg, #15A9F2 0%, #2C73E8 58%, #1E5AD0 100%)';
const PAGE = '#0A0A0A';
const LOGO = '/logo-darkplanner.png';
// Avatar do ATENDENTE (Planner) — aparece no header do home, no chat e nas conversas.
const AVATAR = '/avatar-planner.png';

const initialOf = (name) => (name || '').trim().charAt(0).toUpperCase() || '?';

// Tempo relativo curto ("há segundos", "há 5 min", "há 2 h", "há 3 d").
function relTime(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'há segundos';
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  return `há ${Math.floor(s / 86400)} d`;
}

function PlannerAvatar({ size = 30 }) {
  return (
    <img src={AVATAR} alt="Planner" style={{
      width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
      boxShadow: '0 2px 8px rgba(13,174,255,0.25)',
    }} />
  );
}

function UserAvatar({ src, name, size = 30 }) {
  if (src) {
    return <img src={src} alt={name || 'Você'} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, background: GRAD, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700,
    }}>{initialOf(name)}</div>
  );
}

export default function HelpWidget({ userName, userAvatar }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('inicio');     // inicio | conversas | ajuda
  const [view, setView] = useState('tabs');     // tabs | chat | article

  // Chat
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chatClosed, setChatClosed] = useState(false); // conversa encerrada (24h) -> só leitura
  const [chatTicket, setChatTicket] = useState(null);  // nº do ticket da conversa aberta

  // Home: input que inicia uma conversa
  const [homeInput, setHomeInput] = useState('');

  // Conversas / Artigos
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articleSearch, setArticleSearch] = useState('');
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [article, setArticle] = useState(null);
  const [articleScrollEl, setArticleScrollEl] = useState(null);
  // Aba Ajuda: navegação categorias -> artigos da categoria (busca sobrepõe).
  const [helpCategory, setHelpCategory] = useState(null);

  const messagesEndRef = useRef(null);
  const firstName = (userName || '').trim().split(' ')[0] || '';

  // Efeito "DIGITANDO" na saudação (estilo Cakto): o subtítulo é datilografado
  // toda vez que o widget abre.
  const subText = t('help.greeting_sub', 'Como podemos te ajudar hoje?');
  const [typedLen, setTypedLen] = useState(0);
  useEffect(() => {
    if (!open) { setTypedLen(0); return undefined; }
    setTypedLen(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTypedLen(i);
      if (i >= subText.length) clearInterval(id);
    }, 26);
    return () => clearInterval(id);
  }, [open, subText]);

  // Segundo "digitando": o PLACEHOLDER do input datilografa em sequência,
  // logo depois que a saudação termina.
  const phText = t('help.ask_planner', 'Escreva aqui sua dúvida...');
  const [phLen, setPhLen] = useState(0);
  useEffect(() => {
    if (!open) { setPhLen(0); return undefined; }
    setPhLen(0);
    let i = 0;
    let id = null;
    const start = setTimeout(() => {
      id = setInterval(() => {
        i += 1;
        setPhLen(i);
        if (i >= phText.length) clearInterval(id);
      }, 26);
    }, subText.length * 26 + 300);
    return () => { clearTimeout(start); if (id) clearInterval(id); };
  }, [open, phText, subText]);
  const typedPlaceholder = phLen < phText.length ? `${phText.slice(0, phLen)}▍` : phText;

  useEffect(() => {
    if (view === 'chat') requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }, [messages, view]);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    setLoadingConvos(true);
    try { const r = await axios.get(`${API}/help/conversations`); setConversations(r.data.conversations || []); }
    catch { /* silencioso */ } finally { setLoadingConvos(false); }
  }, []);

  const loadArticles = useCallback(async (q) => {
    setLoadingArticles(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      const r = await axios.get(`${API}/help/articles?${params.toString()}`);
      setArticles(r.data.articles || []);
    } catch { /* silencioso */ } finally { setLoadingArticles(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadArticles('');           // home preview + aba Ajuda
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || tab !== 'ajuda') return;
    const id = setTimeout(() => loadArticles(articleSearch.trim()), 300);
    return () => clearTimeout(id);
  }, [articleSearch, open, tab, loadArticles]);

  // ── Chat ───────────────────────────────────────────────────────────────
  const openConversation = async (c) => {
    setConversationId(c.id); setMessages([]); setView('chat'); setChatClosed(false);
    setChatTicket(c.ticket_number || null);
    try {
      const r = await axios.get(`${API}/help/conversations/${c.id}/messages`);
      setMessages(r.data.messages || []);
      setChatClosed(!!r.data.is_closed);
      if (r.data.ticket_number) setChatTicket(r.data.ticket_number);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao abrir conversa'); }
  };

  const reopenChat = async () => {
    if (!conversationId) return;
    try { await axios.post(`${API}/help/conversations/${conversationId}/reopen`); setChatClosed(false); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao reabrir conversa'); }
  };

  // explicitMsg: texto a enviar (default = input). convOverride: força conversation_id
  // (usado ao iniciar do home, onde o setState de conversationId ainda não propagou).
  const send = async (explicitMsg, convOverride) => {
    const msg = (explicitMsg ?? input).trim();
    if (!msg || sending) return;
    if (msg.length > MAX_CHARS) { toast.error(t('help.too_long', `Máximo de ${MAX_CHARS} caracteres.`)); return; }
    const cid = convOverride !== undefined ? convOverride : conversationId;
    setSending(true);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    try {
      const res = await axios.post(`${API}/help/chat`, { message: msg, conversation_id: cid });
      setConversationId(res.data.conversation_id);
      if (res.data.ticket_number) setChatTicket(res.data.ticket_number);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || t('help.chat_error', 'Não consegui responder agora. Tente de novo em instantes.');
      setMessages((prev) => [...prev, { role: 'assistant', content: detail, error: true }]);
      if (status === 429 || status === 400) toast.error(detail);
    } finally { setSending(false); }
  };

  const startChatFromHome = () => {
    const msg = homeInput.trim();
    setConversationId(null); setMessages([]); setView('chat'); setTab('inicio'); setHomeInput(''); setChatClosed(false); setChatTicket(null);
    if (msg) send(msg, null);
  };

  const onChatKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const onHomeKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); startChatFromHome(); } };
  const goToHelpCenter = () => { setOpen(false); navigate('/central-de-ajuda'); };
  const openArticle = (a) => { setArticle(a); setView('article'); };

  // Categorias (ordem alfabética, igual à página) + artigos da categoria ativa.
  const helpCategories = useMemo(() => {
    const set = [];
    for (const a of articles) {
      const c = a.categoria || 'Geral';
      if (!set.includes(c)) set.push(c);
    }
    return set;
  }, [articles]);
  const helpSearching = !!articleSearch.trim();
  const categoryArticles = useMemo(
    () => (helpCategory ? articles.filter((a) => (a.categoria || 'Geral') === helpCategory) : []),
    [articles, helpCategory],
  );
  // Sai da aba Ajuda -> volta pra lista de categorias na próxima visita.
  useEffect(() => { if (tab !== 'ajuda') setHelpCategory(null); }, [tab]);

  // Visibilidade GLOBAL do widget (super admin liga/desliga na Central de Ajuda).
  // null = carregando (não renderiza nada, evita flash). Falha de rede NUNCA esconde.
  const [widgetEnabled, setWidgetEnabled] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/help/widget-config`)
      .then((r) => { if (alive) setWidgetEnabled(r.data?.enabled !== false); })
      .catch(() => { if (alive) setWidgetEnabled(true); });
    return () => { alive = false; };
  }, []);

  // Widget desligado pelo super admin (ou config ainda carregando): não renderiza.
  if (widgetEnabled !== true) return null;

  const isHome = view === 'tabs' && tab === 'inicio';

  return (
    <>
      {/* Botão flutuante: chat (fechado) ⇄ seta pra baixo p/ MINIMIZAR (aberto) */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t('help.minimize', 'Minimizar') : t('help.open', 'Abrir ajuda')}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.92 }}
        style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 902,
          width: '48px', height: '48px', borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: GRAD, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(13,174,255,0.38)',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? 'min' : 'open'}
            initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.5 }}
            transition={{ duration: 0.18 }}
            style={{ display: 'flex' }}
          >
            {open ? <ChevronDown size={22} /> : <MessageSquareMore size={21} />}
          </motion.span>
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="help-panel"
            initial={{ opacity: 0, scale: 0.92, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.8 }}
            style={{
              position: 'fixed', bottom: '84px', right: '24px', zIndex: 901,
              // No modo ARTIGO o painel só ALARGA (mesma altura) pra leitura confortável.
              width: view === 'article' ? 'min(720px, calc(100vw - 32px))' : 'min(390px, calc(100vw - 32px))',
              height: 'min(600px, calc(100vh - 120px))',
              transition: 'width .28s cubic-bezier(.4,0,.2,1)',
              transformOrigin: 'bottom right',
              background: PAGE, borderRadius: '20px', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
              display: 'flex', flexDirection: 'column', fontFamily: 'var(--dp-font)',
            }}
          >
      {/* ═══════════ HEADER ═══════════ */}
      {isHome ? (
        // Header RICO (tela inicial) — gradiente GRANDE e suave que melta no fundo,
        // descendo até perto do input (estilo Cakto)
        <div style={{ position: 'relative', background: HEADER_GRAD, padding: '16px 20px 66px', color: '#fff', flexShrink: 0 }}>
          {/* brilho sutil no topo (profundidade) */}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(115% 80% at 82% -15%, rgba(255,255,255,0.16), transparent 58%)', pointerEvents: 'none' }} />
          {/* MELT alto e suave: o azul se dissolve devagar até o fundo da página */}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '120px', background: `linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0.55) 62%, ${PAGE} 100%)`, pointerEvents: 'none' }} />
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <img src={LOGO} alt="DarkPlanner" style={{ width: '28px', height: '28px', borderRadius: '9px', objectFit: 'cover', boxShadow: '0 4px 14px rgba(0,0,0,0.35)' }} />
            <button onClick={() => setOpen(false)} style={hdrIconBtn} aria-label="Fechar"><X size={18} /></button>
          </div>
          {/* Avatar do ATENDENTE (estilo Cakto: foto da equipe acima da saudação) */}
          <img src={AVATAR} alt="Suporte DarkPlanner" style={{
            position: 'relative', width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover',
            display: 'block', marginTop: '12px', border: '2px solid rgba(255,255,255,0.55)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
          }} />
          <div style={{ position: 'relative', marginTop: '10px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.12 }}>
              {t('help.greeting', 'Olá')} {firstName} 👋
            </div>
            <div style={{ fontSize: '14px', fontWeight: 400, opacity: 0.92, marginTop: '5px', minHeight: '21px' }}>
              {subText.slice(0, typedLen)}
              {typedLen < subText.length && <span style={{ opacity: 0.85 }}>▍</span>}
            </div>
          </div>
        </div>
      ) : (
        // Header COMPACTO (chat / artigo / conversas / ajuda)
        <div style={{ position: 'relative', background: HEADER_GRAD, padding: '14px 16px', color: '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            {(view === 'chat' || view === 'article') ? (
              <button onClick={() => setView('tabs')} style={hdrIconBtn} aria-label="Voltar"><ArrowLeft size={18} /></button>
            ) : null}
            {view === 'chat' ? <PlannerAvatar size={28} /> : null}
            <span style={{ fontWeight: 700, fontSize: '15px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {view === 'chat' ? t('help.assistant', 'Planner · Assistente')
                : view === 'article' ? (article?.titulo || t('help.article_label', 'Artigo'))
                : tab === 'conversas' ? t('help.tab_convos', 'Conversas')
                : t('help.tab_help', 'Ajuda')}
            </span>
            {view === 'chat' && chatTicket && (
              <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 700, background: 'rgba(255,255,255,0.18)', borderRadius: '6px', padding: '2px 7px' }}>
                #{chatTicket}
              </span>
            )}
          </div>
          <button onClick={() => setOpen(false)} style={hdrIconBtn} aria-label="Fechar"><X size={18} /></button>
        </div>
      )}

      {/* ═══════════ BODY ═══════════ */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* ── CHAT ── */}
        {view === 'chat' && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {messages.length === 0 && !sending && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginTop: '20px', textAlign: 'center' }}>
                  <PlannerAvatar size={44} />
                  <div style={{ fontSize: '13.5px', color: 'var(--dp-text-secondary)', maxWidth: '240px', lineHeight: 1.5 }}>
                    {t('help.chat_intro', 'Manda sua dúvida que eu te ajudo com base na Central de Ajuda.')}
                  </div>
                </div>
              )}
              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10, x: isUser ? 14 : -14, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
                    transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                    style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexDirection: isUser ? 'row-reverse' : 'row' }}
                  >
                    {isUser ? <UserAvatar src={userAvatar} name={userName} size={28} /> : <PlannerAvatar size={28} />}
                    <div style={{
                      maxWidth: '78%', padding: '10px 13px', fontSize: '13.5px', lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: isUser ? GRAD : '#1A1A1A',
                      color: isUser ? '#fff' : (m.error ? '#F87171' : 'var(--dp-text-primary)'),
                      border: isUser ? 'none' : '1px solid rgba(255,255,255,0.06)',
                      borderRadius: '16px',
                      borderBottomRightRadius: isUser ? '5px' : '16px',
                      borderBottomLeftRadius: isUser ? '16px' : '5px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}>
                      {isUser ? m.content : (
                        // Resposta do Planner em markdown leve (último <p> sem margem via wrapper)
                        <div style={{ marginBottom: '-8px', whiteSpace: 'normal' }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={CHAT_MD_COMPONENTS} skipHtml>
                            {m.content}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
              {sending && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
                  style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}
                >
                  <PlannerAvatar size={28} />
                  <div style={{ padding: '11px 14px', background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', borderBottomLeftRadius: '5px', display: 'flex', alignItems: 'center', gap: '7px', color: 'var(--dp-text-tertiary)', fontSize: '12.5px' }}>
                    <Loader2 size={14} className="dp-spin" /> {t('help.thinking', 'digitando...')}
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <ChatComposer input={input} setInput={setInput} onKeyDown={onChatKeyDown} onSend={() => send()} sending={sending} closed={chatClosed} onReopen={reopenChat} t={t} />
          </>
        )}

        {/* ── ARTIGO (mesmo ArticleView da página) ── */}
        {view === 'article' && article && (
          <div ref={setArticleScrollEl} style={{ flex: 1, overflowY: 'auto', padding: '18px' }}>
            <ArticleView
              key={article.id}
              article={article}
              layout="widget"
              scrollRoot={articleScrollEl}
              onFullscreen={() => { setOpen(false); navigate(`/central-de-ajuda?article=${article.id}`); }}
            />
          </div>
        )}

        {/* ── TABS ── */}
        {view === 'tabs' && (
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isHome ? '4px 16px 88px' : '16px 16px 88px' }}>
            {/* INÍCIO: input central + última conversa + bloco Central de Ajuda */}
            {tab === 'inicio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Input arredondado MENOR e CENTRALIZADO (estilo Cakto) — cola no melt do gradiente */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', width: '100%', maxWidth: '330px',
                  margin: '0 auto', boxSizing: 'border-box',
                  background: '#141414', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '999px',
                  padding: '4px 4px 4px 18px', boxShadow: '0 12px 30px rgba(0,0,0,0.45)',
                  transition: 'border-color .2s, box-shadow .2s',
                }}>
                  <input
                    value={homeInput} onChange={(e) => setHomeInput(e.target.value)} onKeyDown={onHomeKeyDown}
                    maxLength={MAX_CHARS} placeholder={typedPlaceholder}
                    style={{
                      flex: 1, minWidth: 0, border: 'none', background: 'none', outline: 'none', padding: '6px 0',
                      color: 'var(--dp-text-primary)', fontSize: '13.5px', fontFamily: 'var(--dp-font)',
                    }}
                    onFocus={(e) => { const w = e.target.parentElement; w.style.borderColor = '#0DAEFF'; w.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
                    onBlur={(e) => { const w = e.target.parentElement; w.style.borderColor = 'rgba(255,255,255,0.08)'; w.style.boxShadow = '0 12px 30px rgba(0,0,0,0.45)'; }}
                  />
                  <button onClick={startChatFromHome} disabled={!homeInput.trim()} aria-label="Enviar" style={{
                    flexShrink: 0, width: '32px', height: '32px', borderRadius: '50%', border: 'none',
                    background: homeInput.trim() ? GRAD : 'rgba(255,255,255,0.08)', color: '#fff',
                    cursor: homeInput.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background .2s, transform .2s',
                  }}
                    onMouseEnter={(e) => { if (homeInput.trim()) { e.currentTarget.style.background = GRAD_HOVER; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = homeInput.trim() ? GRAD : 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  ><ArrowUp size={15} /></button>
                </div>

                {/* ÚLTIMA CONVERSA — retomar de onde parou (estilo Cakto) */}
                {conversations.length > 0 && (() => {
                  const c = conversations[0];
                  return (
                    <button onClick={() => openConversation(c)} style={card()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                        <PlannerAvatar size={34} />
                        <div style={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                            {c.ticket_number && (
                              <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 700, color: '#0DAEFF' }}>#{c.ticket_number}</span>
                            )}
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                              {c.last_message || c.titulo || t('help.conversation', 'Conversa')}
                            </span>
                          </div>
                          <div style={{ fontSize: '10.5px', color: 'var(--dp-text-tertiary)', marginTop: 3 }}>
                            Planner · {relTime(c.updated_at)}
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={16} color="var(--dp-text-tertiary)" style={{ flexShrink: 0 }} />
                    </button>
                  );
                })()}

                {/* Bloco Central de Ajuda */}
                <div style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)' }}>
                      <HelpCircle size={16} color="#0DAEFF" /> {t('help.center', 'Central de Ajuda')}
                    </span>
                    <button onClick={goToHelpCenter} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0DAEFF', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
                      {t('help.see_all', 'Ver tudo')} <ChevronRight size={13} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {loadingArticles ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}><Loader2 size={20} className="dp-spin" color="#0DAEFF" /></div>
                    ) : articles.length === 0 ? (
                      <div style={{ padding: '0 16px 16px', fontSize: '12.5px', color: 'var(--dp-text-tertiary)' }}>{t('help.empty', 'Nenhum artigo publicado ainda.')}</div>
                    ) : (
                      articles.slice(0, 5).map((a) => <ArticleRow key={a.id} a={a} onClick={() => openArticle(a)} />)
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* CONVERSAS */}
            {tab === 'conversas' && (
              loadingConvos ? (
                <Center><Loader2 size={22} className="dp-spin" color="#0DAEFF" /></Center>
              ) : conversations.length === 0 ? (
                <Empty text={t('help.no_convos', 'Você ainda não tem conversas.')} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {conversations.map((c) => (
                    <button key={c.id} onClick={() => openConversation(c)} style={card()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                        <PlannerAvatar size={30} />
                        <div style={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                            {c.ticket_number && (
                              <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 700, color: '#0DAEFF' }}>#{c.ticket_number}</span>
                            )}
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                              {c.titulo || t('help.conversation', 'Conversa')}
                            </span>
                          </div>
                          {c.last_message && (
                            <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>
                              {c.last_message}
                            </div>
                          )}
                          <div style={{ fontSize: '10.5px', color: 'var(--dp-text-tertiary)', marginTop: 2 }}>
                            {c.is_closed && <span style={{ color: '#F59E0B' }}>Encerrada · </span>}
                            Planner · {relTime(c.updated_at)}
                          </div>
                        </div>
                      </div>
                      <ChevronRight size={16} color="var(--dp-text-tertiary)" style={{ flexShrink: 0 }} />
                    </button>
                  ))}
                </div>
              )
            )}

            {/* AJUDA */}
            {tab === 'ajuda' && (
              <div>
                <div style={{ position: 'relative', marginBottom: '14px' }}>
                  <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--dp-text-tertiary)' }} />
                  <input value={articleSearch} onChange={(e) => setArticleSearch(e.target.value)}
                    placeholder={t('help.search_placeholder', 'Busque por uma dúvida...')}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(10,10,10,0.6)', color: 'var(--dp-text-primary)', fontSize: '13px', fontFamily: 'var(--dp-font)', outline: 'none' }}
                    onFocus={(e) => { e.target.style.borderColor = '#0DAEFF'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }} />
                </div>
                {loadingArticles ? (
                  <Center><Loader2 size={20} className="dp-spin" color="#0DAEFF" /></Center>
                ) : helpSearching ? (
                  /* BUSCA ativa: resultados diretos (ignora categorias) */
                  articles.length === 0 ? (
                    <Empty text={t('help.no_results', 'Nenhum artigo encontrado.')} />
                  ) : (
                    <div style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                      {articles.map((a) => <ArticleRow key={a.id} a={a} onClick={() => openArticle(a)} />)}
                    </div>
                  )
                ) : helpCategory ? (
                  /* CATEGORIA aberta: voltar + artigos dela */
                  <div>
                    <button onClick={() => setHelpCategory(null)} style={{
                      display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px',
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      color: '#0DAEFF', fontSize: '12.5px', fontFamily: 'var(--dp-font)', fontWeight: 600,
                    }}>
                      <ArrowLeft size={14} /> {t('help.all_categories', 'Todas as categorias')}
                    </button>
                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 10px' }}>{helpCategory}</div>
                    <div style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                      {categoryArticles.map((a) => <ArticleRow key={a.id} a={a} onClick={() => openArticle(a)} />)}
                    </div>
                  </div>
                ) : helpCategories.length === 0 ? (
                  <Empty text={t('help.empty', 'Nenhum artigo publicado ainda.')} />
                ) : (
                  /* HOME da aba: CARDS DE CATEGORIA (estilo Cakto) */
                  <div style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden' }}>
                    {helpCategories.map((cat) => {
                      const Icon = categoryIcon(cat);
                      return (
                        <button key={cat} onClick={() => setHelpCategory(cat)} style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                          padding: '13px 16px', background: 'none', border: 'none',
                          borderTop: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer',
                          transition: 'background .15s', textAlign: 'left',
                        }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#1A1A1A'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                            <Icon size={16} color="#0DAEFF" style={{ flexShrink: 0 }} />
                            <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat}</span>
                          </span>
                          <ChevronRight size={16} color="var(--dp-text-tertiary)" style={{ flexShrink: 0 }} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ BOTTOM NAV — pílula ESTREITA centralizada (estilo Cakto) ═══════════ */}
        {view === 'tabs' && (
          <div style={{
            // Mesma largura do input "Escreva aqui sua dúvida" (330px centralizado).
            position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: '12px', zIndex: 3,
            width: 'calc(100% - 32px)', maxWidth: '330px', boxSizing: 'border-box',
            display: 'flex', gap: '4px',
            background: 'rgba(22,22,22,0.18)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.07)', borderRadius: '999px', padding: '3px 5px',
            boxShadow: '0 8px 26px rgba(0,0,0,0.3)',
          }}>
            {[
              { key: 'inicio', label: t('help.tab_home', 'Início'), Icon: Home },
              { key: 'conversas', label: t('help.tab_convos', 'Conversas'), Icon: MessagesSquare },
              { key: 'ajuda', label: t('help.tab_help', 'Ajuda'), Icon: HelpCircle },
            ].map(({ key, label, Icon }) => {
              const on = tab === key;
              return (
                <button key={key} onClick={() => setTab(key)} style={{
                  flex: 1, padding: '4px 8px', border: 'none', background: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px',
                  color: on ? '#0DAEFF' : 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                  borderRadius: '999px', transition: 'color .2s', whiteSpace: 'nowrap',
                }}>
                  <Icon size={18} strokeWidth={on ? 2.4 : 2} />
                  <span style={{ fontSize: '10.5px', fontWeight: on ? 700 : 500 }}>{label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── Subcomponentes ───────────────────────────────────────────────────────────
function ChatComposer({ input, setInput, onKeyDown, onSend, sending, closed, onReopen, t }) {
  if (closed) {
    return (
      <div style={{ padding: '16px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', background: '#0E0E0E', textAlign: 'center' }}>
        <div style={{ fontSize: '12.5px', color: 'var(--dp-text-tertiary)', marginBottom: '10px' }}>
          {t('help.closed', 'Esta conversa foi encerrada.')}
        </div>
        <button onClick={onReopen} style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '999px',
          border: 'none', cursor: 'pointer', background: GRAD, color: '#fff', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)',
        }}>{t('help.reopen', 'Reabrir conversa')}</button>
      </div>
    );
  }
  return (
    <div style={{ padding: '10px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', background: '#0E0E0E' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <textarea
          value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
          maxLength={MAX_CHARS} rows={1} placeholder={t('help.type_message', 'Escreva sua mensagem...')}
          style={{
            flex: 1, resize: 'none', maxHeight: '110px', padding: '10px 13px', borderRadius: '13px',
            border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(10,10,10,0.6)',
            color: 'var(--dp-text-primary)', fontSize: '13.5px', fontFamily: 'var(--dp-font)', outline: 'none',
            transition: 'border-color .2s, box-shadow .2s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#0DAEFF'; e.target.style.boxShadow = '0 0 0 3px rgba(13,174,255,0.12)'; }}
          onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
        />
        <button onClick={onSend} disabled={sending || !input.trim()} aria-label="Enviar" style={{
          flexShrink: 0, width: '40px', height: '40px', borderRadius: '13px', border: 'none',
          background: input.trim() && !sending ? GRAD : 'rgba(255,255,255,0.06)', color: '#fff',
          cursor: input.trim() && !sending ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background .2s, transform .2s',
        }}
          onMouseEnter={(e) => { if (input.trim() && !sending) { e.currentTarget.style.background = GRAD_HOVER; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.background = input.trim() && !sending ? GRAD : 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        ><Send size={16} /></button>
      </div>
      {/* Limite de 1000 chars continua ATIVO (maxLength + validação no send) — só sem contador visível. */}
    </div>
  );
}

function ArticleRow({ a, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
      padding: '15px 18px', background: 'none', border: 'none', borderTop: '1px solid rgba(255,255,255,0.04)',
      cursor: 'pointer', transition: 'background .15s', textAlign: 'left',
    }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#1A1A1A'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.titulo}</div>
        {a.resumo && <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '3px' }}>{a.resumo}</div>}
      </div>
      <ChevronRight size={16} color="var(--dp-text-tertiary)" style={{ flexShrink: 0 }} />
    </button>
  );
}

const Center = ({ children }) => <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>{children}</div>;
const Empty = ({ text }) => <div style={{ textAlign: 'center', color: 'var(--dp-text-tertiary)', padding: '40px 10px', fontSize: '13px' }}>{text}</div>;

const hdrIconBtn = {
  background: 'rgba(255,255,255,0.16)', border: 'none', borderRadius: '9px', cursor: 'pointer',
  color: '#fff', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, transition: 'background .2s',
};
const card = () => ({
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
  padding: '12px 14px', borderRadius: '14px', cursor: 'pointer',
  background: '#141414', border: '1px solid rgba(255,255,255,0.06)', transition: 'border-color .2s',
});
